import React, { useEffect, useState } from "react";
import { WarningOctagon, ShieldWarning, Question } from "@phosphor-icons/react";

// Promise-based confirmation for serious actions. Usage:
//   if (!(await confirmAction({ title, description, confirmLabel, severity: "danger", typeToConfirm: "CANCEL" }))) return;
let _open = null;
export function confirmAction(opts) {
  return new Promise((resolve) => {
    if (!_open) { resolve(window.confirm(`${opts.title}\n\n${opts.description || ""}`)); return; }
    _open({ ...opts, resolve });
  });
}

const ICON = { danger: WarningOctagon, warn: ShieldWarning, info: Question };
const TONE = {
  danger: { ring: "border-red-200", icon: "text-red-600 bg-red-50", btn: "bg-red-600 hover:bg-red-700 text-white" },
  warn: { ring: "border-amber-200", icon: "text-amber-700 bg-amber-50", btn: "bg-black hover:bg-neutral-800 text-white" },
  info: { ring: "border-black/10", icon: "text-[#0A84FF] bg-blue-50", btn: "bg-[#0A84FF] hover:bg-[#0870d8] text-white" },
};

export default function ConfirmHost() {
  const [req, setReq] = useState(null);
  const [typed, setTyped] = useState("");
  useEffect(() => { _open = (r) => { setTyped(""); setReq(r); }; return () => { _open = null; }; }, []);
  useEffect(() => {
    if (!req) return;
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  if (!req) return null;
  const close = (ok) => { req.resolve(ok); setReq(null); };
  const sev = req.severity || "warn";
  const Icon = ICON[sev] || ShieldWarning;
  const t = TONE[sev] || TONE.warn;
  const needsTyped = !!req.typeToConfirm;
  const ready = !needsTyped || typed.trim().toUpperCase() === String(req.typeToConfirm).toUpperCase();
  return (
    <div className="fixed inset-0 z-[10000] bg-black/55 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={() => close(false)} data-testid="confirm-dialog">
      <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-md border ${t.ring} p-5`} onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${t.icon}`}><Icon size={22} weight="fill" /></div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Please confirm</div>
            <h3 className="font-display font-black text-lg text-black leading-tight mt-0.5" data-testid="confirm-title">{req.title}</h3>
            {req.description && <p className="text-sm text-neutral-700 mt-2" data-testid="confirm-description">{req.description}</p>}
          </div>
        </div>
        {needsTyped && (
          <label className="block mt-4 text-xs font-semibold text-neutral-700">
            Type <span className="font-black text-black">{req.typeToConfirm}</span> to confirm
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="confirm-typed"
              className="mt-1 w-full border border-black/15 rounded-md px-3 py-2 text-sm font-mono tracking-widest uppercase focus:outline-none focus:border-red-500" />
          </label>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => close(false)} data-testid="confirm-cancel" className="px-4 py-2 rounded-md text-sm font-semibold border border-black/15 hover:bg-neutral-50">{req.cancelLabel || "No, go back"}</button>
          <button onClick={() => ready && close(true)} disabled={!ready} data-testid="confirm-ok"
            className={`px-4 py-2 rounded-md text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed ${t.btn}`}>{req.confirmLabel || "Yes, continue"}</button>
        </div>
      </div>
    </div>
  );
}
